/*
 * Calli product page: gallery, purchase options, dispatch date and sticky add-to-cart bar.
 * Progressive enhancement: without JS the gallery still swipes and the default option still adds to cart.
 */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------- Gallery: swipe track + thumbnails + arrows ---------- */
  function initGallery(gallery) {
    const track = gallery.querySelector('[data-track]');
    const slides = [...gallery.querySelectorAll('[data-slide]')];
    const thumbsWrap = gallery.querySelector('[data-thumbs]');
    const prev = gallery.querySelector('[data-prev]');
    const next = gallery.querySelector('[data-next]');
    if (!track || slides.length === 0) return;

    if (slides.length < 2) {
      prev?.remove();
      next?.remove();
      thumbsWrap?.remove();
      return;
    }

    // Thumbnails reuse each slide's image; sizes="64px" lets the browser pick the smallest file.
    thumbsWrap.innerHTML = '';
    const thumbs = slides.map((slide, i) => {
      const img = slide.querySelector('img');
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'calli-pdp__thumb' + (slide.classList.contains('calli-pdp__slide--tile') ? ' is-tile' : '');
      btn.setAttribute('aria-label', `Show image ${i + 1}`);
      btn.style.cssText = slide.style.cssText;
      if (img) {
        const t = img.cloneNode();
        t.removeAttribute('fetchpriority');
        t.setAttribute('sizes', '64px');
        t.loading = 'lazy';
        t.alt = '';
        btn.appendChild(t);
      }
      btn.addEventListener('click', () => go(i));
      thumbsWrap.appendChild(btn);
      return btn;
    });

    let current = -1;
    function setActive(i) {
      if (i === current) return;
      current = i;
      thumbs.forEach((t, n) => t.setAttribute('aria-current', n === i ? 'true' : 'false'));
      if (prev) prev.disabled = i === 0;
      if (next) next.disabled = i === slides.length - 1;
      const thumb = thumbs[i];
      if (thumb) {
        const left = thumb.offsetLeft - thumbsWrap.clientWidth / 2 + thumb.clientWidth / 2;
        thumbsWrap.scrollTo({ left, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      }
    }
    function go(i) {
      const n = Math.max(0, Math.min(slides.length - 1, i));
      track.scrollTo({ left: slides[n].offsetLeft, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      setActive(n);
    }

    let raf = 0;
    track.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => setActive(Math.round(track.scrollLeft / track.clientWidth)));
      },
      { passive: true }
    );
    prev?.addEventListener('click', () => go(current - 1));
    next?.addEventListener('click', () => go(current + 1));
    track.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        go(current + 1);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        go(current - 1);
      }
    });
    setActive(0);
  }

  /* ---------- Purchase options: point Horizon's product form at the chosen product + plan ---------- */
  function initPlans(root) {
    const radios = [...root.querySelectorAll('.calli-plan input[type="radio"]')];
    const formEl = root.querySelector('[data-calli-form]');
    if (!radios.length || !formEl) return;
    const variantInput = formEl.querySelector('[data-calli-variant]');
    const planInput = formEl.querySelector('[data-calli-plan]');
    const label = formEl.querySelector('[data-calli-label]');
    const price = formEl.querySelector('[data-calli-price]');
    const stickyPrice = root.querySelector('[data-calli-sticky-price]');
    const stickyCup = root.querySelector('[data-calli-sticky-cup]');
    const stickyLabel = root.querySelector('[data-calli-sticky-label]');
    const delivery = root.querySelector('[data-calli-delivery]');

    function apply(radio) {
      const d = radio.dataset;
      if (variantInput) variantInput.value = d.variant;
      if (planInput) {
        planInput.value = d.plan || '';
        // A disabled input is left out of the form data, so one-time orders carry no selling plan.
        planInput.disabled = !d.plan;
      }
      formEl.dataset.productId = d.product;
      const productIdInput = formEl.querySelector('input[name="product-id"]');
      if (productIdInput) productIdInput.value = d.product;
      if (label) label.textContent = d.label;
      if (price) price.textContent = d.price;
      if (stickyPrice) stickyPrice.textContent = d.price;
      if (stickyCup) stickyCup.textContent = d.perCup;
      if (stickyLabel) stickyLabel.textContent = d.label;
      // Free delivery is a subscription perk, so the delivery point follows the selected plan.
      if (delivery) {
        const text = radio.value === 'once' ? delivery.dataset.once : delivery.dataset.sub;
        delivery.querySelector('span').textContent = text || '';
        delivery.hidden = !text;
      }
    }

    radios.forEach((radio) => radio.addEventListener('change', () => radio.checked && apply(radio)));
    const checked = radios.find((r) => r.checked) || radios[0];
    if (checked) {
      checked.checked = true;
      apply(checked);
    }
  }

  /* ---------- Dispatch date: N working days from today (Singapore time) ---------- */
  function initEta(root) {
    const el = root.querySelector('[data-calli-eta]');
    if (!el) return;
    const out = el.querySelector('[data-calli-eta-date]');
    let days = parseInt(el.dataset.days, 10) || 0;
    // Today's date in Singapore, whatever the visitor's own timezone.
    const sg = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Singapore' }));
    const date = new Date(sg.getFullYear(), sg.getMonth(), sg.getDate());
    const isWeekend = (d) => d.getDay() === 0 || d.getDay() === 6;
    while (isWeekend(date)) date.setDate(date.getDate() + 1);
    while (days > 0) {
      date.setDate(date.getDate() + 1);
      if (!isWeekend(date)) days--;
    }
    if (out) out.textContent = date.toLocaleDateString('en-SG', { weekday: 'short', day: 'numeric', month: 'short' });
    el.classList.add('is-ready');
  }

  /* ---------- Sticky add-to-cart bar ---------- */
  function initSticky(root) {
    const bar = root.querySelector('[data-calli-sticky]');
    const atc = root.querySelector('[data-calli-atc]');
    if (!bar || !atc) return;
    const btn = bar.querySelector('[data-calli-sticky-btn]');

    function show(visible) {
      bar.classList.toggle('is-visible', visible);
      bar.setAttribute('aria-hidden', visible ? 'false' : 'true');
      if (btn) btn.tabIndex = visible ? 0 : -1;
    }

    // Visible only once the main button has scrolled up out of view (not before you reach it).
    new IntersectionObserver(([entry]) => {
      show(!entry.isIntersecting && entry.boundingClientRect.top < 0);
    }).observe(atc);

    btn?.addEventListener('click', () => {
      if (atc.disabled) return;
      atc.click();
    });
  }

  function init(scope) {
    scope.querySelectorAll('[data-calli-pdp]').forEach((root) => {
      if (root.dataset.calliReady) return;
      root.dataset.calliReady = 'true';
      root.querySelectorAll('[data-calli-gallery]').forEach(initGallery);
      initPlans(root);
      initEta(root);
      initSticky(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document));
  } else {
    init(document);
  }
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
